// @ts-check

/**
 * @typedef {{ retail?: number|null, educator?: number|null, school?: number|null, currency?: string }} PriceObj
 */

/** @param {unknown} value */
const asNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Old book/bundle records may still carry stale AED/CAD currency tags from before
 * the storefront was locked to USD. Keep the numeric amount and normalize the label.
 *
 * @param {unknown} value
 * @param {string} fallbackCurrency
 */
const normalizeCurrency = (value, fallbackCurrency) => {
  const raw = typeof value === 'string' ? value.toUpperCase() : '';
  if (!raw) return fallbackCurrency;
  if (fallbackCurrency === 'USD' && (raw === 'AED' || raw === 'CAD')) return 'USD';
  return raw;
};

/**
 * Normalize any known backend/frontend price shape into a storefront price object.
 * Supports:
 * - `number` (legacy)
 * - `{ retail, educator, school, currency }` (storefront)
 * - `{ retailPrice, educatorPrice, schoolPrice, currency }` (Book prices[] element)
 * - `{ bundlePrice, currency }` (Bundle controller projection)
 *
 * @param {unknown} priceLike
 * @param {string} [fallbackCurrency='USD']
 * @returns {PriceObj|null}
 */
export const toPriceObject = (priceLike, fallbackCurrency = 'USD') => {
  if (priceLike == null) return null;

  const normalizedFallbackCurrency = normalizeCurrency(fallbackCurrency, 'USD');

  const numeric = asNumber(priceLike);
  if (numeric !== null) {
    return {
      retail: numeric,
      educator: numeric,
      school: numeric,
      currency: normalizedFallbackCurrency,
    };
  }

  if (typeof priceLike !== 'object') return null;

  const raw = /** @type {any} */ (priceLike);
  const currency = normalizeCurrency(raw.currency, normalizedFallbackCurrency);

  const retail =
    asNumber(raw.retail)
    ?? asNumber(raw.retailPrice)
    ?? asNumber(raw.bundlePrice)
    ?? asNumber(raw.price)
    ?? null;

  const educator = asNumber(raw.educator) ?? asNumber(raw.educatorPrice) ?? retail;
  const school = asNumber(raw.school) ?? asNumber(raw.schoolPrice) ?? retail;

  if (retail === null && educator === null && school === null) return null;

  return { retail, educator, school, currency };
};

/**
 * Returns the most relevant displayable numeric value from a price-like input.
 * @param {unknown} priceLike
 * @returns {number|null}
 */
export const getDisplayPrice = (priceLike) => {
  const normalized = toPriceObject(priceLike);
  return normalized?.retail ?? null;
};
